import React, { createContext, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PartialPaymentFinancialTransactionModal from "../../components/modals/partialPaymentFinancialTransactionModal";
import SettleFinancialTransactionModal from "../../components/modals/settleFinancialTransactionModal";
import { useCurrencyContext } from "../../hooks/useCurrencyContext";
import { useFinancialReducer } from "../../hooks/useFinancialReducer";
import { findAllFarmSells } from "../../services/farmSellService";
import { generateFinancialCashFlow } from "../../services/financialCashFlowService";
import { generateFinancialCosting } from "../../services/financialCostingService";
import { findAllFinancialParametersByGroupIdAndFarmId } from "../../services/financialParameterService";
import { pageFinancialTransactions } from "../../services/financialTransactionsService";

export const FinancialContext = createContext(null);

export const FinancialProvider = ({ children }) => {
  const [
    tablePropertiesFinancialTransactions,
    setTablePropertiesFinancialTransactions,
  ] = useState({
    size: 10,
    page: 0,
    tableFilters: {},
    tableSorters: {},
    search: "",
  });

  const [
    selectedFinancialTransactionElements,
    setSelectedFinancialTransactionElements,
  ] = useState([]);

  const [
    selectedFinancialTransactionKeys,
    setSelectedFinancialTransactionKeys,
  ] = useState([]);

  const [
    shouldShowModalSettledFinancialTransaction,
    setShouldShowModalSettledFinancialTransaction,
  ] = useState(false);

  const [
    shouldShowModalPartialPaymentFinancialTransaction,
    setShouldShowModalPartialPaymentFinancialTransaction,
  ] = useState(false);

  const [financialTransactionData, setFinancialTransactionData] =
    useState(null);

  const [financialTransactionDataEdit, setFinancialTransactionDataEdit] =
    useState(null);

  const [
    modalSettledFinancialTransactionAction,
    setModalSettledFinancialTransactionAction,
  ] = useState("new");

  const { defaultCurrencyCode } = useCurrencyContext();

  const {
    state: {
      listFinancialParameters,
      isLoadingListFinancialParameters,
      listFinancialCashFlow,
      isLoadingListFinancialCashFlow,
      listFinancialCosting,
      isLoadingListFinancialCosting,
      shouldShowDrawerFinancialCashFlowBudget,
      isLoadingListFinancialTransaction,
      listFinancialTransaction,
      shouldShowFormFinancialTransaction,
      listFarmSells,
      isLoadingFarmSells,
    },
    dispatch: financialDispatch,
  } = useFinancialReducer();

  const {
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  // Methods
  const openOrCloseDrawerFinancialCashFlowBudget = useCallback(() => {
    financialDispatch({
      type: "SHOW_OR_HIDE_DRAWER_FINANCIAL_CASH_FLOW_BUDGET",
    });
  }, [financialDispatch]);

  const refreshListFinancialParameters = useCallback(async () => {
    try {
      financialDispatch({
        type: "START_STOP_LOADING_LIST_FINANCIAL_PARAMETERS",
      });
      const {
        data: { results },
      } = await findAllFinancialParametersByGroupIdAndFarmId({
        groupId,
        farmId,
      });
      financialDispatch({
        type: "UPDATE_LIST_FINANCIAL_PARAMETERS",
        payload: {
          data: results,
        },
      });
    } catch (error) {
    } finally {
      financialDispatch({
        type: "START_STOP_LOADING_LIST_FINANCIAL_PARAMETERS",
      });
    }
  }, [farmId, financialDispatch, groupId]);

  const refreshListFinancialCashFlow = useCallback(
    async (financialProjectId = null, bankAccountFarmId = null) => {
      if (defaultCurrencyCode) {
        try {
          financialDispatch({
            type: "START_STOP_LOADING_LIST_FINANCIAL_CASH_FLOW",
          });
          const {
            data: { results },
          } = await generateFinancialCashFlow({
            groupId,
            farmId,
            financialProjectId,
            bankAccountFarmId,
            currency: defaultCurrencyCode ?? "BRL",
          });
          financialDispatch({
            type: "UPDATE_LIST_FINANCIAL_CASH_FLOW",
            payload: {
              data: results,
            },
          });
        } catch (error) {
        } finally {
          financialDispatch({
            type: "START_STOP_LOADING_LIST_FINANCIAL_CASH_FLOW",
          });
        }
      }
    },
    [farmId, financialDispatch, groupId, defaultCurrencyCode]
  );

  const refreshListFinancialCosting = useCallback(
    async (financialProjectId = null) => {
      if (defaultCurrencyCode) {
        try {
          financialDispatch({
            type: "START_STOP_LOADING_LIST_FINANCIAL_COSTING",
          });
          const {
            data: { results },
          } = await generateFinancialCosting({
            groupId,
            farmId,
            financialProjectId,
            currency: defaultCurrencyCode ?? "BRL",
          });
          financialDispatch({
            type: "UPDATE_LIST_FINANCIAL_COSTING",
            payload: {
              data: results,
            },
          });
        } catch (error) {
        } finally {
          financialDispatch({
            type: "START_STOP_LOADING_LIST_FINANCIAL_COSTING",
          });
        }
      }
    },
    [farmId, financialDispatch, groupId, defaultCurrencyCode]
  );

  const refreshListFinancialTransaction = useCallback(
    async (page = 0, size = 10, sort = {}, search = "") => {
      try {
        financialDispatch({
          type: "START_STOP_LOADING_LIST_FINANCIAL_TRANSACTIONS",
        });
        const {
          data: { results },
        } = await pageFinancialTransactions({
          groupId,
          farmId,
          page,
          size,
          sort,
          search,
        });
        financialDispatch({
          type: "UPDATE_LIST_FINANCIAL_TRANSACTIONS",
          payload: {
            data: results,
          },
        });
      } catch (error) {
      } finally {
        financialDispatch({
          type: "START_STOP_LOADING_LIST_FINANCIAL_TRANSACTIONS",
        });
      }
    },
    [farmId, financialDispatch, groupId]
  );

  const openOrCloseDrawerFinancialTransactionForm = useCallback(() => {
    financialDispatch({
      type: "SHOW_OR_HIDE_FORM_FINANCIAL_TRANSACTION",
    });
  }, [financialDispatch]);

  const refreshListFarmSells = useCallback(async () => {
    try {
      financialDispatch({
        type: "START_STOP_LOADING_LIST_FARM_SELLS",
      });
      const {
        data: { results },
      } = await findAllFarmSells({
        groupId,
        farmId,
      });
      financialDispatch({
        type: "UPDATE_LIST_FARM_SELLS",
        payload: {
          data: results,
        },
      });
    } catch (error) {
    } finally {
      financialDispatch({
        type: "START_STOP_LOADING_LIST_FARM_SELLS",
      });
    }
  }, [farmId, financialDispatch, groupId]);

  const handleSelectFinancialTransaction = useCallback((records) => {
    const keys = records.map((record) => record.id);
    setSelectedFinancialTransactionKeys(keys);
    setSelectedFinancialTransactionElements(records);
  }, []);

  const handleOpenModalSettled = useCallback((action = "new") => {
    setShouldShowModalSettledFinancialTransaction(true);
    setModalSettledFinancialTransactionAction(action);
  }, []);

  const handleCloseModalSettled = useCallback(() => {
    setSelectedFinancialTransactionKeys([]);
    setSelectedFinancialTransactionElements([]);
    setShouldShowModalSettledFinancialTransaction(false);
    setModalSettledFinancialTransactionAction("new");
  }, []);

  const handleOpenModalPartialPayment = useCallback((financialTransaction) => {
    setFinancialTransactionData(financialTransaction);
    setShouldShowModalPartialPaymentFinancialTransaction(true);
  }, []);

  const handleCloseModalPartialPayment = useCallback(() => {
    setFinancialTransactionData(null);
    setShouldShowModalPartialPaymentFinancialTransaction(false);
  }, []);

  const handleSetFinancialTransactionDataToEdit = useCallback((data) => {
    setFinancialTransactionDataEdit(data);
  }, []);

  // Effects

  // LOAD PARAMETERS
  useEffect(() => {
    if (groupId && farmId) refreshListFinancialParameters();

    return () => {
      financialDispatch({
        type: "UPDATE_LIST_FINANCIAL_PARAMETERS",
        payload: {
          data: [],
        },
      });
    };
  }, [farmId, financialDispatch, groupId, refreshListFinancialParameters]);

  // LOAD CASH FLOW
  useEffect(() => {
    if (farmId && groupId && defaultCurrencyCode)
      refreshListFinancialCashFlow();

    return () => {
      financialDispatch({
        type: "UPDATE_LIST_FINANCIAL_CASH_FLOW",
        payload: {
          data: [],
        },
      });
    };
  }, [
    farmId,
    financialDispatch,
    groupId,
    defaultCurrencyCode,
    refreshListFinancialCashFlow,
  ]);

  // LOAD COSTING
  useEffect(() => {
    if (farmId && groupId && defaultCurrencyCode) refreshListFinancialCosting();
    return () => {
      financialDispatch({
        type: "UPDATE_LIST_FINANCIAL_COSTING",
        payload: {
          data: [],
        },
      });
    };
  }, [
    farmId,
    financialDispatch,
    groupId,
    defaultCurrencyCode,
    refreshListFinancialCosting,
  ]);

  // LOAD TRANSACTIONS
  useEffect(() => {
    if (groupId && farmId) refreshListFinancialTransaction();
    return () => {
      financialDispatch({
        type: "UPDATE_LIST_FINANCIAL_TRANSACTIONS",
        payload: {
          data: [],
        },
      });
    };
  }, [farmId, financialDispatch, groupId, refreshListFinancialTransaction]);

  // LOAD FARM SELLS
  useEffect(() => {
    if (groupId && farmId) refreshListFarmSells();
    return () => {
      financialDispatch({
        type: "UPDATE_LIST_FARM_SELLS",
        payload: {
          data: [],
        },
      });
    };
  }, [farmId, financialDispatch, groupId, refreshListFarmSells]);

  return (
    <FinancialContext.Provider
      value={{
        tablePropertiesFinancialTransactions,
        setTablePropertiesFinancialTransactions,
        listFinancialParameters,
        isLoadingListFinancialParameters,
        refreshListFinancialParameters,
        listFinancialCashFlow,
        isLoadingListFinancialCashFlow,
        refreshListFinancialCashFlow,
        listFinancialCosting,
        isLoadingListFinancialCosting,
        refreshListFinancialCosting,
        shouldShowDrawerFinancialCashFlowBudget,
        openOrCloseDrawerFinancialCashFlowBudget,
        isLoadingListFinancialTransaction,
        listFinancialTransaction,
        shouldShowFormFinancialTransaction,
        refreshListFinancialTransaction,
        openOrCloseDrawerFinancialTransactionForm,
        listFarmSells,
        isLoadingFarmSells,
        refreshListFarmSells,
        selectedFinancialTransactionElements,
        selectedFinancialTransactionKeys,
        handleSelectFinancialTransaction,
        shouldShowModalSettledFinancialTransaction,
        modalSettledFinancialTransactionAction,
        handleOpenModalSettled,
        handleCloseModalSettled,
        shouldShowModalPartialPaymentFinancialTransaction,
        financialTransactionData,
        handleOpenModalPartialPayment,
        handleCloseModalPartialPayment,
        financialTransactionDataEdit,
        handleSetFinancialTransactionDataToEdit,
      }}
    >
      {children}
      <SettleFinancialTransactionModal />
      <PartialPaymentFinancialTransactionModal />
    </FinancialContext.Provider>
  );
};
